import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import type { AppState, AppAction, ProcessedData } from '../types';
import { loadData, updateGeoFeaturesForYear } from '../utils/dataLoader';

// Initial state
const initialState: AppState = {
  selectedYear: 2024,
  selectedStates: [],
  compareMode: false,
  hoveredState: null,
  isPlaying: false,
  playSpeed: 1000, // 1 second per year
  topN: 10,
  highContrastMode: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_YEAR':
      return { ...state, selectedYear: action.payload };

    case 'SELECT_STATE':
      if (state.compareMode) {
        // In compare mode, allow up to 3 states
        if (state.selectedStates.includes(action.payload)) {
          return state; // Already selected
        }
        if (state.selectedStates.length >= 3) {
          // Replace the oldest selection
          return {
            ...state,
            selectedStates: [...state.selectedStates.slice(1), action.payload],
          };
        }
        return {
          ...state,
          selectedStates: [...state.selectedStates, action.payload],
        };
      }
      // Normal mode - single selection
      return {
        ...state,
        selectedStates: [action.payload],
      };

    case 'DESELECT_STATE':
      return {
        ...state,
        selectedStates: state.selectedStates.filter((s) => s !== action.payload),
      };

    case 'CLEAR_SELECTION':
      return { ...state, selectedStates: [] };

    case 'TOGGLE_COMPARE_MODE':
      return {
        ...state,
        compareMode: !state.compareMode,
        selectedStates: state.compareMode ? state.selectedStates.slice(0, 1) : state.selectedStates,
      };

    case 'SET_HOVERED_STATE':
      return { ...state, hoveredState: action.payload };

    case 'SET_PLAYING':
      return { ...state, isPlaying: action.payload };

    case 'SET_PLAY_SPEED':
      return { ...state, playSpeed: action.payload };

    case 'SET_TOP_N':
      return { ...state, topN: action.payload };

    case 'TOGGLE_HIGH_CONTRAST':
      return { ...state, highContrastMode: !state.highContrastMode };

    default:
      return state;
  }
}

// Context types
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  data: ProcessedData | null;
  isLoading: boolean;
  error: string | null;
  // Derived data
  currentYearSummary: ProcessedData['yearSummaries'] extends Map<number, infer T> ? T | undefined : never;
  geoFeaturesWithData: ProcessedData['geoFeatures'];
  // Actions
  setYear: (year: number) => void;
  selectState: (state: string) => void;
  deselectState: (state: string) => void;
  clearSelection: () => void;
  toggleCompareMode: () => void;
  setHoveredState: (state: string | null) => void;
  startPlaying: () => void;
  stopPlaying: () => void;
  togglePlaying: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

// Provider component
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [data, setData] = useState<ProcessedData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data on mount
  useEffect(() => {
    loadData()
      .then((loadedData) => {
        setData(loadedData);
        // Set initial year to latest available
        if (loadedData.years.length > 0) {
          const latestYear = loadedData.years[loadedData.years.length - 1];
          dispatch({ type: 'SET_YEAR', payload: latestYear });
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load data:', err);
        setError('Failed to load data. Please refresh the page.');
        setIsLoading(false);
      });
  }, []);

  // Derived: current year summary
  const currentYearSummary = useMemo(() => {
    if (!data) return undefined;
    return data.yearSummaries.get(state.selectedYear);
  }, [data, state.selectedYear]);

  // Derived: geo features with current year data
  const geoFeaturesWithData = useMemo(() => {
    if (!data || !currentYearSummary) return data?.geoFeatures ?? [];
    return updateGeoFeaturesForYear(
      data.geoFeatures,
      currentYearSummary,
      data.stateSummaries
    );
  }, [data, currentYearSummary]);

  // Action helpers
  const setYear = useCallback((year: number) => {
    dispatch({ type: 'SET_YEAR', payload: year });
  }, []);

  const selectState = useCallback((stateName: string) => {
    dispatch({ type: 'SELECT_STATE', payload: stateName });
  }, []);

  const deselectState = useCallback((stateName: string) => {
    dispatch({ type: 'DESELECT_STATE', payload: stateName });
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  const toggleCompareMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_COMPARE_MODE' });
  }, []);

  const setHoveredState = useCallback((stateName: string | null) => {
    dispatch({ type: 'SET_HOVERED_STATE', payload: stateName });
  }, []);

  const startPlaying = useCallback(() => {
    dispatch({ type: 'SET_PLAYING', payload: true });
  }, []);

  const stopPlaying = useCallback(() => {
    dispatch({ type: 'SET_PLAYING', payload: false });
  }, []);

  const togglePlaying = useCallback(() => {
    dispatch({ type: 'SET_PLAYING', payload: !state.isPlaying });
  }, [state.isPlaying]);

  // Year animation
  useEffect(() => {
    if (!state.isPlaying || !data) return;

    const interval = setInterval(() => {
      dispatch({ type: 'SET_YEAR', payload: state.selectedYear });
      
      const currentIndex = data.years.indexOf(state.selectedYear);
      const nextIndex = (currentIndex + 1) % data.years.length;
      const nextYear = data.years[nextIndex];
      
      if (nextIndex === 0) {
        // Reached the end, stop playing
        dispatch({ type: 'SET_PLAYING', payload: false });
      } else {
        dispatch({ type: 'SET_YEAR', payload: nextYear });
      }
    }, state.playSpeed);

    return () => clearInterval(interval);
  }, [state.isPlaying, state.selectedYear, state.playSpeed, data]);

  const contextValue: AppContextType = {
    state,
    dispatch,
    data,
    isLoading,
    error,
    currentYearSummary,
    geoFeaturesWithData,
    setYear,
    selectState,
    deselectState,
    clearSelection,
    toggleCompareMode,
    setHoveredState,
    startPlaying,
    stopPlaying,
    togglePlaying,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}

// Hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Hook for just the state
export function useAppState() {
  const { state } = useApp();
  return state;
}

// Hook for just the data
export function useAppData() {
  const { data, isLoading, error } = useApp();
  return { data, isLoading, error };
}

